import React, { useCallback, useEffect, useState } from 'react';
import { Box, Checkbox, FormControlLabel, InputBase, makeStyles, Typography } from '@material-ui/core';
import { i18n } from '../../../../language';
import { bedStatusAPI, eadmissionAPI } from '../../../../apis';
import { AutoCompleteSelect } from '../../../../components';
import { getTransferInOutAdmissionTypeList } from '../../../../utils/newBedStatusUtil';

const useStyles = makeStyles((theme) => ({
  root: {
    background: '#fff',
    borderRadius: '0 0.93rem 0.93rem 0.93rem',
  },
  checkBox: {
    '& .MuiCheckbox-root': {
      color: '#c0c6cb',
    },
    '& .MuiTypography-root': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '1.142rem',
      fontWeight: '500',
      color: '#2e4469',
    },
    '& .Mui-checked': {
      color: '#28d2ab',
    },
  },
  title: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1.375rem',
    fontWeight: 'bold',
    color: '#2e4469',
    marginBottom: '0.5rem',
  },
  inputText: {
    '& *': {
      padding: '0',
      margin: '0',
      border: '0',
    },
    width: '15rem',
    fontFamily: 'NotoSans-Medium',
    flex: '50%',
    height: '2.875rem',
    lineHeight: '2.875rem',
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'center',
    borderRadius: '0.625rem',
    border: 'solid 1px #e4e7eb',
    backgroundColor: '#fff',
    padding: '0 1.25rem',
    color: '#2e4469',
    fontSize: '1rem',
    marginTop: '0.875rem',
    '& .MuiInputBase-root': {
      fontFamily: 'NotoSans-Medium',
      color: '#2e4469',
    },
    '& .MuiInputBase-input': {
      height: '3.5rem',
      lineHeight: '3.5rem',
    },
    '& :focus': {
      backgroundColor: 'unset',
    },
    '& :before': {
      content: 'none',
    },
    ':before': {
      content: 'none !important',
    },
    '& :after': {
      content: 'none',
    },
  },
}));

export default function TransferIn() {
  const classes = useStyles();
  const [visibleSetting, setVisibleSetting] = useState({
    eAdmVisible: false,
    requestInVisible: false,
    requestInViewAll: false,
    defaultAdmType: '',
    defaultFromUnit: '',
  });
  const [admTypeList, setAdmTypeList] = useState([]);

  const getAdmissionSetting = useCallback(async () => {
    try {
      const { result } = await eadmissionAPI.getTransferInVisible();
      setVisibleSetting({
        eAdmVisible: result?.EADM_VIEW_VISIBLE === 1,
        requestInVisible: result?.REQUEST_IN_VIEW_VISIBLE === 1,
        requestInViewAll: result?.REQUEST_IN_VIEW_ALL === 1,
        defaultAdmType: result?.DEFAULT_ADMISSION_TYPE ?? '',
        defaultFromUnit: result?.DEFAULT_FROM_UNIT ?? '',
      });
    } catch (error) {}
  }, []);

  // 用最新的setting值更新Transfer In配置。
  const updateAdmissionSetting = useCallback(
    async (nextSetting) => {
      try {
        const params = {
          eAdmVisible: nextSetting?.eAdmVisible ? 1 : 0,
          reqInVisible: nextSetting?.requestInVisible ? 1 : 0,
          reqInViewAll: nextSetting?.requestInViewAll ? 1 : 0,
          fromUnit: nextSetting?.defaultFromUnit,
          admissionType: nextSetting?.defaultAdmType,
        };
        const { statusCode } = await eadmissionAPI.updateTransferInVisible(params);
        if (statusCode === '0') {
          await getAdmissionSetting();
        }
      } catch (error) {}
    },
    [getAdmissionSetting]
  );

  const handleUpdateSetting = useCallback(
    (key, value) => {
      // checkbox和下拉变更后立即用下一份setting call api。
      const nextSetting = { ...visibleSetting, [key]: value };
      setVisibleSetting(nextSetting);
      updateAdmissionSetting(nextSetting);
    },
    [updateAdmissionSetting, visibleSetting]
  );

  // 输入框先只更新本地值，避免每个字都call api。
  const handleDefaultFromUnitChange = useCallback((event) => {
    const { value } = event.target;
    setVisibleSetting((prev) => ({ ...prev, defaultFromUnit: value }));
  }, []);

  // 输入框失焦时再保存默认From Unit。
  const handleDefaultFromUnitBlur = useCallback(
    (event) => {
      const nextSetting = { ...visibleSetting, defaultFromUnit: event.target.value };
      setVisibleSetting(nextSetting);
      updateAdmissionSetting(nextSetting);
    },
    [updateAdmissionSetting, visibleSetting]
  );

  const getAdmTypeList = useCallback(async () => {
    try {
      const { result } = await bedStatusAPI.getBsMlookupList();
      const admTypeList = getTransferInOutAdmissionTypeList(result);
      admTypeList.unshift({ text: '', value: '' });
      setAdmTypeList(admTypeList);
    } catch (error) {}
  }, []);

  useEffect(() => {
    getAdmissionSetting();
    getAdmTypeList();
  }, []);

  return (
    <div className={classes.root}>
      <Box mb="2rem">
        <Typography className={classes.title}>{i18n.t('EADMISSION_TRANSFER_IN_SETTING_EADM_LABEL')}</Typography>
        <FormControlLabel
          id="eadmission_setting_enable_eadm_checkbox"
          control={<Checkbox />}
          label={i18n.t('EADMISSION_TRANSFER_IN_SETTING_ENABLE_EADM')}
          className={classes.checkBox}
          checked={visibleSetting?.eAdmVisible}
          onChange={(event) => handleUpdateSetting('eAdmVisible', event.target.checked)}
        />
      </Box>
      <Box>
        <Typography className={classes.title}>{i18n.t('EADMISSION_TRANSFER_IN_SETTING_NON_EADM_LABEL')}</Typography>
        <Box display="flex" flexDirection="column" gridGap="0.875rem">
          <FormControlLabel
            id="eadmission_setting_enable_transfer_in_checkbox"
            control={<Checkbox />}
            label={i18n.t('EADMISSION_TRANSFER_IN_SETTING_CONTENT')}
            className={classes.checkBox}
            checked={visibleSetting?.requestInVisible}
            onChange={(event) => handleUpdateSetting('requestInVisible', event.target.checked)}
          />
          <FormControlLabel
            id="eadmission_setting_enable_transfer_in_view_all_units_checkbox"
            control={<Checkbox />}
            label={i18n.t('EADMISSION_TRANSFER_IN_SETTING_VIEW_ALL_UNITS')}
            className={classes.checkBox}
            checked={visibleSetting?.requestInViewAll}
            onChange={(event) => handleUpdateSetting('requestInViewAll', event.target.checked)}
          />
          <Box>
            <Typography>{i18n.t('EADMISSION_TRANSFER_IN_SETTING_DEFAULT_FROM_UNIT')}</Typography>
            <InputBase
              className={classes.inputText}
              placeholder="Input"
              onChange={handleDefaultFromUnitChange}
              onBlur={handleDefaultFromUnitBlur}
              value={visibleSetting?.defaultFromUnit}
            />
          </Box>
          <Box mt="0.875rem">
            <Typography style={{ marginBottom: '0.875rem' }}>
              {i18n.t('EADMISSION_TRANSFER_IN_SETTING_DEFAULT_ADM_TYPE')}
            </Typography>
            <AutoCompleteSelect
              id="eadmission_setting_default_admission_type"
              itemList={admTypeList}
              placeholder={i18n.t('BEDSTATUS_SELECT_FROM_BELOW_ITEMS')}
              value={visibleSetting?.defaultAdmType}
              onChange={(value) => handleUpdateSetting('defaultAdmType', value)}
              width="15rem"
            />
          </Box>
        </Box>
      </Box>
    </div>
  );
}
